import React, { useEffect, useMemo, useState } from "react";
import {
  loadApplicationData,
  loadProductData,
} from "../../services/ApiService";
import dataContainer from "../../services/DataContainer";
import { HiddenOptions } from "../../enum/enums";
import { DataContainerContext } from ".";
import {
  ActiveComponent,
  ActiveComponentGroup,
  Component,
  CurrentLocaleModel,
  Product,
  VTGComponent,
  VTGComponentGroup,
} from "./types";

export const DataContainerProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const [data, setData] = useState<Product[]>([]);
  const [currentLocale, setCurrentLocale] = useState<CurrentLocaleModel>({
    locale: "en-en",
    localeID: "2",
  });
  const [activeComponentIndex, setActiveComponentIndex] = useState(0);
  const [modelName, setModelName] = useState("");
  const [searchParams, setSearchParams] = useState<string[]>();
  const [defaultVariantColor, setDefaultVariantColor] = useState("");
  const [isColorComingFromURL, setIsColorComingFromURL] = useState(false);
  const [productDataLoaded, setProductDataLoaded] = useState(false);

  const allComponents: VTGComponent[] = useMemo(() => {
    const groups: VTGComponentGroup[] =
      dataContainer
        .getComponentGroups()
        ?.filter(
          (componentGroup: VTGComponentGroup) =>
            componentGroup.name !== HiddenOptions.HIDDEN_OPTIONS
        ) || [];
    return groups.flatMap((group, groupIndex: number) =>
      [...(group.components ?? [])]
        .sort((a, b) => a.order_no - b.order_no)
        .map((component: Component, idxInGroup: number) => ({
          ...component,
          idxInGroup,
          groupIndex,
          groupName: group.name,
          groupId: group.id,
        }))
    );
  }, [data, productDataLoaded]);

  const activeComponent = useMemo((): ActiveComponent => {
    const component = allComponents[activeComponentIndex];
    if (!component) {
      return { name: "", componentId: 0, index: 0 };
    }
    return {
      name: component.name,
      componentId: component.id,
      index: component.idxInGroup,
    };
  }, [allComponents, activeComponentIndex]);

  const activeComponentGroups = useMemo((): ActiveComponentGroup => {
    const component = allComponents[activeComponentIndex];
    if (!component) {
      return { name: "", id: 0, index: 0 };
    }
    return {
      name: component.groupName,
      id: component.groupId,
      index: component.groupIndex,
    };
  }, [allComponents, activeComponentIndex]);

  const goNext = () => {
    if (activeComponentIndex < allComponents.length - 1) {
      setActiveComponentIndex((i: number) => i + 1);
      return true;
    }
    return false;
  };

  const goBack = () => {
    if (activeComponentIndex > 0) {
      setActiveComponentIndex((i: number) => i - 1);
      return true;
    }
    return false;
  };

  const jumpToGroup = (group: string | number) => {
    let targetIndex = -1;
    if (typeof group === "number") {
      targetIndex = allComponents.findIndex((c) => c.groupIndex === group);
    } else {
      targetIndex = allComponents.findIndex((c) => c.groupName === group);
    }

    if (targetIndex !== -1) {
      setActiveComponentIndex(targetIndex);
    }
  };

  const jumpToComponent = (name: string) => {
    const targetIndex = allComponents.findIndex(
      (c) =>
        c.groupIndex === activeComponentGroups.index && c.name === name
    );
    if (targetIndex !== -1) {
      setActiveComponentIndex(targetIndex);
    }
  };

  useEffect(() => {
    if (!currentLocale) {
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      try {
        const [res, app] = await Promise.all([
          loadProductData(currentLocale.locale),
          loadApplicationData(currentLocale.locale),
        ]);
        if (cancelled) {
          return;
        }
        const productData = res.data.data;
        const appData = app.data.data;
        dataContainer.mApplicationData = appData;
        dataContainer.mProducts = productData;
        setData(productData);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [currentLocale]);

  return (
    <DataContainerContext.Provider
      value={{
        data,
        currentLocale,
        setCurrentLocale,
        activeComponent,
        activeComponentGroups,
        activeComponentIndex,
        setActiveComponentIndex,
        allComponents,
        goNext,
        goBack,
        jumpToGroup,
        jumpToComponent,
        modelName,
        setModelName,
        searchParams,
        setSearchParams,
        defaultVariantColor,
        setDefaultVariantColor,
        isColorComingFromURL,
        setIsColorComingFromURL,
        setProductDataLoaded,
      }}
    >
      {children}
    </DataContainerContext.Provider>
  );
};
